// Screen: "Fairness"
import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const SECTIONS = [
  {
    icon: '\uD83C\uDFB2',
    title: 'Card Shuffling',
    content: 'Every deal uses a cryptographically secure pseudo-random number generator (CSPRNG) via Node.js crypto.getRandomValues(). This is the same standard used by online casinos and security applications. No human or AI player can predict or influence the shuffle.'
  },
  {
    icon: '\uD83E\uDD16',
    title: 'AI Fairness',
    content: 'AI players cannot see your cards. They make decisions based only on publicly available information: cards played, trump suit, and game history. AI decision-making parameters are trained through simulations and are identical for all AI opponents.'
  },
  {
    icon: '\uD83D\uDD12',
    title: 'Anti-Cheating',
    content: 'We monitor for suspicious patterns including win trading (repeatedly playing the same opponent with lopsided results), multi-accounting (multiple accounts from the same device), and collusion in multiplayer games. Flagged accounts are reviewed by administrators.'
  },
  {
    icon: '\u23F1\uFE0F',
    title: 'Disconnect Policy',
    content: 'If a player disconnects during a multiplayer game, they have a 90-second grace period to reconnect. After that, AI takes over their seat and the disconnected player receives the final result. This prevents intentional disconnects to avoid losses.'
  },
  {
    icon: '\uD83D\uDCCA',
    title: 'ELO Rating System',
    content: 'Player ratings use the ELO system (K-factor of 32 for new players, 16 for experienced players). Ratings only change in multiplayer games against other humans. Solo AI games do not affect your rating, ensuring competitive integrity.'
  },
  {
    icon: '\uD83D\uDCB0',
    title: 'Economy Integrity',
    content: 'All Bucks transactions are logged and auditable. Balance changes are validated server-side — the client cannot manipulate balances. Administrators monitor the total Bucks in circulation to maintain economy health.'
  }
];

export default function FairnessPage() {
  const isMobile = useIsMobile();

  return (
    <>
    <SEO
      title="Fairness & Transparency"
      description="How Euchre Table ensures fair play with cryptographic shuffling, AI transparency, and anti-cheating measures."
      path="/fairness"
    />
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 24px',
      maxWidth: 700, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 24 }}>Fairness & Transparency</h1>

      {/* Intro */}
      <div style={{
        background: 'linear-gradient(135deg, #1c2333, #0d1117)',
        border: '1px solid #238636', borderRadius: 12,
        padding: isMobile ? 16 : 20, marginBottom: 20
      }}>
        <div style={{ fontSize: isMobile ? 13 : 14, color: '#c9d1d9', lineHeight: 1.6 }}>
          We are committed to providing a fair and transparent gaming experience. This page explains how we ensure integrity in every aspect of the game.
        </div>
      </div>

      {/* Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {SECTIONS.map((section, i) => (
          <div key={i} style={{
            background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
            padding: isMobile ? 16 : 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 20 }}>{section.icon}</span>
              <h2 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, margin: 0 }}>{section.title}</h2>
            </div>
            <p style={{ fontSize: isMobile ? 12 : 13, color: '#8b949e', lineHeight: 1.7, margin: 0 }}>
              {section.content}
            </p>
          </div>
        ))}
      </div>

      {/* Contact */}
      <div style={{
        background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
        padding: isMobile ? 16 : 20, marginTop: 12, textAlign: 'center'
      }}>
        <div style={{ fontSize: 13, color: '#8b949e' }}>
          Have concerns about fairness? Contact us at <span style={{ color: '#58a6ff' }}>support@euchretable.com</span>
        </div>
      </div>
    </div>
    </>
  );
}
